import yup from 'yup';
import _ from 'lodash';
import moment from 'moment';

import { initialClaimForm, ERROR_REQUIRED, ERROR_EMAIL } from './constants';
import { UpdateClaim } from '../../mutations/UpdateClaim';
import { noUTC } from '../DateAndTimeInputs';
import mapConnection from '../../utils/connectionMapper';

export const formikConfig = {
  enableReinitialize: true,
  mapPropsToValues: ({ claim }) => {
    if (claim) {
      const currentClaim = {
        ...initialClaimForm,
        notRemovableFlightId: claim.flight.id,
        secretToken: claim.token,
        airlineAmicableAnswer: claim.airlineAmicableAnswer,
        airlinePreLitigationAnswer: claim.airlinePreLitigationAnswer,
        overallDelayAtDestination: claim.overallDelayAtDestination,
        lawyerFormalNoticeDate:
          claim.legalNormalNotice && claim.legalFormalNotice.sentDate
            ? noUTC(claim.legalFormalNotice.sentDate)
            : undefined,
        passengerTicketId: claim.passengerTicketId,
        flightId: claim.flightId,
        passengers: mapConnection(claim.claimsPassengerTicketConnection).map(
          p => ({
            ...p.passengerTicket,
            affiliate: p.passengerTicket.affiliate.id
          })
        ),
        flights: mapConnection(claim.flightsPassengerTicketsConnection).map(
          ({ flight }) => ({
            ...flight,
            airline: flight.airline.iata,
            localArrivalDate: moment(flight.localArrivalDate).isValid()
              ? noUTC(flight.localArrivalDate)
              : undefined,
            localDepartureDate: moment(flight.localDepartureDate).isValid()
              ? noUTC(flight.localDepartureDate)
              : undefined,
            airportOrigin: flight.airportOrigin.iata,
            airportDestination: flight.airportDestination.iata
          })
        ),
        id: claim.codeClaim,
        lawyer: _.get(claim, 'lawyer.id', ''),
        litigationCountry: _.get(claim, 'litigationCountry', ''),
        nebSentDate: moment(claim.nebSentDate).isValid()
          ? noUTC(claim.nebSentDate)
          : undefined,
        courtId: claim.courtId,
        conciliationAt: moment(claim.conciliationAt).isValid()
          ? noUTC(claim.conciliationAt)
          : undefined,
        nRg: claim.nRg,
        courtHearingAt: moment(claim.courtHearingAt).isValid()
          ? noUTC(claim.courtHearingAt)
          : undefined,
        courtDecisionDate: moment(claim.courtDecisionDate).isValid()
          ? noUTC(claim.courtecisionDate)
          : undefined,
        paxAgreementAmount: claim.paxAgreementAmount,
        airlineAgreementDate: moment(claim.airlineAgreementDate).isValid()
          ? noUTC(claim.airlineAgreementDate)
          : undefined,
        settlementType: claim.settlementType,
        legalFees: claim.legalFees,
        refundFromLawyerDate: moment(claim.refundFromLawyerDate).isValid()
          ? noUTC(claim.refundFromLawyerDate)
          : undefined,
        refundFromLawyerAmountForPax:
          claim.refundFromLawyerAmountForPax,
        refundFromLawyerLegalFeesAmount:
          claim.refundFromLawyerLegalFeesAmount,
        paxTransferDate: moment(claim.paxTransferDate).isValid()
          ? noUTC(claim.paxTransferDate)
          : undefined,
        paxToTransferAmount: claim.paxToTransferAmount
      };
      return { ...currentClaim };
    }
    return { ...initialClaimForm };
  },
  validationSchema: () => {
    return yup.object().shape({
      secretToken: yup
        .string()
        .required(ERROR_REQUIRED)
        .min(10, 'Minimum 10 characters'),
      flights: yup.array().of(
        yup.object().shape({
          airportOrigin: yup.string().required(ERROR_REQUIRED),
          airportDestination: yup.string().required(ERROR_REQUIRED),
          airline: yup.string().required(ERROR_REQUIRED),
          localDepartureDate: yup.string().required(ERROR_REQUIRED),
          localArrivalDate: yup.string().required(ERROR_REQUIRED)
        })
      ),
      passengers: yup.array().of(
        yup.object().shape({
          passengerFirstName: yup.string().required(ERROR_REQUIRED),
          passengerLastName: yup.string().required(ERROR_REQUIRED),
          passengerLanguage: yup.string().required(ERROR_REQUIRED),
          passengerEmail: yup
            .string()
            .email(ERROR_EMAIL)
            .required(ERROR_REQUIRED),
          affiliate: yup.string().required(ERROR_REQUIRED),
          number: yup.string().required(ERROR_REQUIRED)
        })
      ),
      lawyer: yup.string().nullable(),
      litigationCountry: yup.string().nullable()
    });
  },
  handleSubmit: (values, { props, setErrors, setSubmitting }) => {
    const crmState = _.get(props, 'claim.crmState', '');
    const flights = [...values.flights];
    const passengers = [...values.passengers];
    const airlineAmicableAnswer = /^c[0-3]|^b/.test(crmState)
      ? values.airlineAmicableAnswer
      : undefined;
    const airlinePreLitigationAnswer = /^c[0-3]/.test(crmState)
      ? values.airlinePreLitigationAnswer
      : undefined;
    const overallDelayAtDestination = values.overallDelayAtDestination;

    const updatedPassengers = passengers.map(p => ({
      pnr: p.pnr,
      id: p.id,
      number: p.number.toString(),
      passengerFirstName: p.passengerFirstName,
      passengerLastName: p.passengerLastName,
      passengerPhone: p.passengerPhone,
      passengerEmail: p.passengerEmail,
      employer: p.employer,
      passengerLanguage: p.passengerLanguage,
      affiliateId: p.affiliate,
      bankName: p.bankName
    }));
    const updatedFlights = flights.map(f => ({
      id: f.id,
      airlineCode: f.airline,
      localDepartureDate: f.localDepartureDate,
      localArrivalDate: f.localArrivalDate,
      airportOriginCode: f.airportOrigin,
      airportDestinationCode: f.airportDestination
    }));
    const litigationFields = crmState.startsWith('c')
      ? {
          nebSentDate:
            crmState === 'c0_national_enforcement_body_sent' &&
            values.nebSentDate !== undefined
              ? values.nebSentDate
              : undefined,
          courtId:
            !crmState.startsWith('c0_national_enforcement_body') &&
            values.courtId !== undefined
              ? values.courtId
              : undefined,
          conciliationAt:
            crmState === 'c4_court_date_fixed' &&
            values.conciliationAt !== undefined
              ? values.conciliationAt
              : undefined,
          nRg:
            crmState === 'c4_court_date_fixed' && values.nRg !== undefined
              ? values.nRg
              : undefined,
          courtHearingAt:
            (crmState === 'c4_court_date_fixed' ||
              crmState === 'c4_post_court_application_negociation' ||
              crmState === 'c4_pending_court_decision' ||
              crmState === 'c4_case_referred_to_court' ||
              /^c[5-9]/.test(crmState)) &&
            values.courtHearingAt !== undefined
              ? values.courtHearingAt
              : undefined,
          courtDecisionDate:
            crmState === 'c4_pending_court_decision' &&
            values.courtDecisionDate !== undefined
              ? values.courtDecisionDate
              : undefined,
          paxAgreementAmount:
            crmState.startsWith('c6') &&
            (values.paxAgreementAmount !== undefined ||
              values.paxAgreementAmount === 0)
              ? parseFloat(Number(values.paxAgreementAmount).toFixed(2))
              : undefined,
          airlineAgreementDate:
            crmState.startsWith('c6') &&
            values.airlineAgreementDate !== undefined
              ? values.airlineAgreementDate
              : undefined,
          settlementType:
            crmState.startsWith('c6') && values.settlementType !== undefined
              ? values.settlementType
              : undefined,
          legalFees:
            crmState.startsWith('c6') &&
            (values.legalFees !== undefined || values.legalFees === 0)
              ? parseFloat(Number(values.legalFees).toFixed(2))
              : undefined,
          refundFromLawyerDate:
            /^c[7-9]/.test(crmState) &&
            values.refundFromLawyerDate !== undefined
              ? values.refundFromLawyerDate
              : undefined,
          refundFromLawyerAmountForPax:
            /^c[7-9]/.test(crmState) &&
            (values.refundFromLawyerAmountForPax !== undefined ||
              values.refundFromLawyerAmountForPax === 0)
              ? parseFloat(
                  Number(values.refundFromLawyerAmountForPax).toFixed(2)
                )
              : undefined,
          refundFromLawyerLegalFeesAmount:
            /^c[7-9]/.test(crmState) &&
            (values.refundFromLawyerLegalFeesAmount !== undefined ||
              values.refundFromLawyerLegalFeesAmount === 0)
              ? parseFloat(
                  Number(values.refundFromLawyerLegalFeesAmount).toFixed(2)
                )
              : undefined,
          paxTransferDate:
            crmState === 'c7_pax_to_refund' &&
            values.paxTransferDate !== undefined
              ? values.paxTransferDate
              : undefined,
          paxToTransferAmount:
            crmState === 'c7_pax_to_refund' &&
            values.paxToTransferAmount !== undefined
              ? values.paxToTransferAmount
              : undefined
        }
      : {};
    const input = {
      claim: {
        id: props.match.params.id,
        token: values.secretToken,
        passengerTicketId: values.passengerTicketId,
        airlineAmicableAnswer: airlineAmicableAnswer,
        airlinePreLitigationAnswer: airlinePreLitigationAnswer,
        overallDelayAtDestination: overallDelayAtDestination,
        lawyerId: values.lawyer === '0' ? null : values.lawyer,
        litigationCountry:
          values.litigationCountry === 'other'
            ? null
            : values.litigationCountry,
        ...litigationFields
      },
      passengerTickets: updatedPassengers,
      flights: updatedFlights,
      legalFormalNotice: {
        sentDate: /^c[4-8]|^c3_litigation_formal_notice_sent/.test(crmState)
          ? values.lawyerFormalNoticeDate
          : undefined
      }
    };
    if (values.comments) {
      input.comment = {
        content: values.comments
      };
    }
    props.client
      .mutate({
        mutation: UpdateClaim,
        variables: {
          input
        }
      })
      .then(() => {
        props.onSubmit();
        setErrors({});
        setSubmitting(false);
      })
      .catch(error => {
        setErrors({ serverError: error });
        window.scrollTo(0, 0);
        setSubmitting(false);
      });
  }
};



// WEBPACK FOOTER //
// ./src/component/ClaimForm/formikConfig.js